import { z } from 'zod';
import { ConversationKind, ReportReason, ReportTarget, RoomState } from './enums';

export const PlayerPublic = z.object({
  publicId: z.string(),
  userId: z.string(),
  displayName: z.string(),
  portraitUrl: z.string().nullable(),
  bio: z.string().nullable(),
  friendship: z.enum(['none', 'pending_out', 'pending_in', 'friends', 'blocked']),
});
export type PlayerPublic = z.infer<typeof PlayerPublic>;

export const FriendRequestCreate = z.object({ publicId: z.string().regex(/^[A-Z0-9]{8}$/) });
export const FriendRequestView = z.object({
  id: z.string(),
  from: PlayerPublic,
  createdAt: z.string(),
});
export type FriendRequestView = z.infer<typeof FriendRequestView>;
export const BlockCreate = z.object({ userId: z.string().max(64) });
export const ProfilePatch = z.object({ bio: z.string().trim().max(120).nullable().optional() });

// ---------- messaging ----------

export const MessageCard = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('story_invite'), templateKey: z.string(), title: z.string() }),
  z.object({ kind: z.literal('room_invite'), roomId: z.string(), title: z.string() }),
  z.object({ kind: z.literal('chapter'), chapterId: z.string(), title: z.string() }),
  /** A neighbour suggests going out somewhere in chat; tapping it asks them out there (same as 約出來). */
  z.object({ kind: z.literal('outing_invite'), npcId: z.string(), locationId: z.string(), placeName: z.string(), pitch: z.string() }),
]);
export type MessageCard = z.infer<typeof MessageCard>;

export const MessageView = z.object({
  id: z.string(),
  seq: z.number().int(),
  senderKind: z.enum(['player', 'npc', 'system']),
  senderId: z.string(),
  senderName: z.string(),
  isAi: z.boolean(),
  body: z.string(),
  card: MessageCard.nullable(),
  clientMessageId: z.string().nullable(),
  createdAt: z.string(),
  mine: z.boolean(),
});
export type MessageView = z.infer<typeof MessageView>;

export const ConversationSummary = z.object({
  id: z.string(),
  kind: ConversationKind,
  title: z.string(),
  avatarUrl: z.string().nullable(),
  isAi: z.boolean(),
  lastMessage: z.string().nullable(),
  lastSeq: z.number().int(),
  unread: z.number().int(),
  muted: z.boolean(),
  updatedAt: z.string(),
  /** NPC chats: who it is, so the header can open their profile. */
  npcId: z.string().nullable().optional(),
  /** NPC chats: messages left today across all neighbours (the app warns when it runs low). */
  npcMessagesLeft: z.number().int().nullable().optional(),
  /** NPC chats: a reply to the player's last message is still being written (the app shows typing and polls). */
  npcReplyPending: z.boolean().optional(),
});
export type ConversationSummary = z.infer<typeof ConversationSummary>;

export const MessagesQuery = z.object({
  afterSeq: z.coerce.number().int().min(0).optional(),
  beforeSeq: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(40),
});
export const SendMessageRequest = z.object({
  clientMessageId: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  body: z.string().trim().min(1).max(1000),
});
export type SendMessageRequest = z.infer<typeof SendMessageRequest>;
export const MarkReadRequest = z.object({ seq: z.number().int().min(0) });
export const OpenNpcConversationRequest = z.object({ npcId: z.string().max(20) });
export const OpenPlayerConversationRequest = z.object({ userId: z.string().max(64) });

// ---------- shared rooms ----------

export const RoomMemberView = z.object({
  userId: z.string(),
  name: z.string(),
  portraitUrl: z.string().nullable(),
  status: z.enum(['invited', 'joined', 'ready', 'left']),
  submittedThisRound: z.boolean(),
  isMe: z.boolean(),
});

export const RoomRoundView = z.object({
  round: z.number().int(),
  title: z.string(),
  narration: z.string(),
  resolution: z.string().nullable(),
  actions: z.array(z.object({ userId: z.string(), name: z.string(), label: z.string(), speech: z.string().nullable() })),
});

export const RoomView = z.object({
  id: z.string(),
  activityKey: z.string(),
  title: z.string(),
  hostUserId: z.string(),
  state: RoomState,
  revision: z.number().int(),
  round: z.number().int(),
  maxRounds: z.number().int(),
  deadlineAt: z.string().nullable(),
  members: z.array(RoomMemberView),
  history: z.array(RoomRoundView),
  current: RoomRoundView.nullable(),
  choices: z.array(z.object({ id: z.string(), label: z.string(), contribution: z.string() })),
  conversationId: z.string().nullable(),
  chapterId: z.string().nullable(),
  consentGiven: z.boolean(),
});
export type RoomView = z.infer<typeof RoomView>;

export const RoomListing = z.object({
  id: z.string(),
  title: z.string(),
  hostName: z.string(),
  hostPortraitUrl: z.string().nullable(),
  locale: z.string(),
  seatsOpen: z.number().int(),
  createdAt: z.string(),
});
export type RoomListing = z.infer<typeof RoomListing>;

export const CreateRoomRequest = z.object({
  activityKey: z.string().max(40),
  visibility: z.enum(['invite', 'board', 'club']),
  clubId: z.string().max(64).optional(),
  inviteUserIds: z.array(z.string().max(64)).max(3).optional(),
  inviteUserId: z.string().max(64).optional(),
  responseWindowHours: z.number().int().min(1).max(72).default(24),
});
export const RoomConsentRequest = z.object({ aiProcessing: z.literal(true), sharedComic: z.literal(true) });
export const RoomActionRequest = z.object({
  round: z.number().int().min(1),
  expectedRevision: z.number().int().min(0),
  choiceId: z.string().max(60),
  speech: z.string().trim().max(200).optional(),
});

// ---------- safety ----------

export const CreateReportRequest = z.object({
  targetType: ReportTarget,
  targetId: z.string().max(64),
  reason: ReportReason,
  note: z.string().max(1000).optional(),
});
export type CreateReportRequest = z.infer<typeof CreateReportRequest>;

export type ProfilePatch = z.infer<typeof ProfilePatch>;
export type FriendRequestCreate = z.infer<typeof FriendRequestCreate>;
export type BlockCreate = z.infer<typeof BlockCreate>;
export type MessagesQuery = z.infer<typeof MessagesQuery>;
export type MarkReadRequest = z.infer<typeof MarkReadRequest>;
export type CreateRoomRequest = z.input<typeof CreateRoomRequest>;
export type RoomConsentRequest = z.infer<typeof RoomConsentRequest>;
export type RoomActionRequest = z.infer<typeof RoomActionRequest>;
